import React, { useState, useEffect } from "react";
import {
  Box, TextField, Button, Typography, Paper, Table, TableHead, 
  TableRow, TableCell, TableBody, Card, Grid, CircularProgress, Autocomplete
} from "@mui/material";
import { db } from "../../firebase";
import { collection, getDocs, query, where, orderBy, onSnapshot, Timestamp } from "firebase/firestore";

function Vehicalwisereport() {
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [vehicleNo, setVehicleNo] = useState("");
  const [vehicleOptions, setVehicleOptions] = useState([]); 
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch unique vehicle numbers for suggestions
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "trips"), (snap) => {
      const vehicles = snap.docs.map(doc => doc.data().vehicleNo);
      const uniqueVehicles = [...new Set(vehicles)].filter(Boolean);
      setVehicleOptions(uniqueVehicles);
    });
    return () => unsub();
  }, []);

  const fetchVehicleReport = async () => {
    if (!vehicleNo) return alert("Please enter or select a Vehicle Number");
    if (!fromDate || !toDate) return alert("Please select a date range");
    
    setLoading(true);

    try {
      // 1. Convert string dates to Firestore Timestamps
      const start = Timestamp.fromDate(new Date(`${fromDate}T00:00:00`));
      const end = Timestamp.fromDate(new Date(`${toDate}T23:59:59`));

      // 2. Query with VehicleNo and Date Range
      const q = query(
        collection(db, "trips"),
        where("vehicleNo", "==", vehicleNo.toUpperCase().trim()),
        where("grossDate", ">=", start),
        where("grossDate", "<=", end),
        orderBy("grossDate", "desc")
      );

      const querySnapshot = await getDocs(q);
      const fetchedTrips = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 3. Flatten nested products array
      let flattenedRows = [];
      fetchedTrips.forEach(trip => {
        const products = trip.products || [];
        products.forEach((p, index) => {
          flattenedRows.push({
            id: `${trip.id}-${index}`,
            date: trip.grossDate,
            partyName: trip.partyName || trip.customerName || "N/A",
            productName: p.productName,
            qty: Number(p.qty) || 0,
            rate: p.rate,
            amount: Number(p.amount) || 0
          });
        });
      });

      setReportData(flattenedRows);
    } catch (error) {
      console.error("Error fetching vehicle report:", error);
      alert("Make sure you have created the required Firestore Index for vehicleNo + date + orderBy.");
    } finally {
      setLoading(false);
    }
  };

  const totalQty = reportData.reduce((sum, item) => sum + item.qty, 0);
  const totalAmt = reportData.reduce((sum, item) => sum + item.amount, 0);

  return (
    <Box p={1}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Vehicle-wise Trip Report
      </Typography>

      <Card sx={{ p: 2, mb: 3 }} className="no-print">
        <Grid className="row" spacing={2} alignItems="center">
          <Grid className="col-sm-3 " md={4}>
            <Autocomplete
              freeSolo
              options={vehicleOptions}
              value={vehicleNo}
              onInputChange={(event, newInputValue) => setVehicleNo(newInputValue)}
              renderInput={(params) => (
                <TextField {...params} label="Vehicle Number" size="small" fullWidth />
              )}
            />
          </Grid>
          <Grid className="col-sm-3" md={2}>
            <TextField
              fullWidth label="From" type="date" size="small"
              InputLabelProps={{ shrink: true }}
              value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid className="col-sm-3" md={2}>
            <TextField
              fullWidth label="To" type="date" size="small"
              InputLabelProps={{ shrink: true }}
              value={toDate} onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
          <Grid className="col-sm-3" md={4}>
            <Button variant="contained" onClick={fetchVehicleReport} disabled={loading} sx={{ mr: 1 }}>
              {loading ? <CircularProgress size={24} /> : "Search"}
            </Button>
            <Button variant="outlined" onClick={() => window.print()}>Print</Button>
          </Grid>
        </Grid>
      </Card>

      <Paper className="printable-area" sx={{ width: '100%', overflow: 'hidden', border: '1px solid #ddd' }}>
        <Box p={2} sx={{ bgcolor: '#fafafa', borderBottom: '1px solid #ddd' }}>
          <Typography variant="subtitle1">
            Results for <strong>{vehicleNo || "N/A"}</strong> ({fromDate} to {toDate})
          </Typography>
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#eeeeee" }}>
            <TableRow>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Party/Customer</strong></TableCell>
              <TableCell><strong>Product Item</strong></TableCell>
              <TableCell align="right"><strong>Qty</strong></TableCell>
              <TableCell align="right"><strong>Rate</strong></TableCell>
              <TableCell align="right"><strong>Total Amount</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.date?.toDate().toLocaleDateString()}</TableCell>
                <TableCell>{row.partyName}</TableCell>
                <TableCell>{row.productName}</TableCell>
                <TableCell align="right">{row.qty}</TableCell>
                <TableCell align="right">{row.rate}</TableCell>
                <TableCell align="right">₹{row.amount.toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {reportData.length > 0 && (
              <TableRow sx={{ bgcolor: "#f0f0f0" }}>
                <TableCell colSpan={3} align="right"><strong>TOTALS</strong></TableCell>
                <TableCell align="right"><strong>{totalQty}</strong></TableCell>
                <TableCell />
                <TableCell align="right"><strong>₹{totalAmt.toLocaleString()}</strong></TableCell>
              </TableRow>
            )}
            {reportData.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No trip data found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area, .printable-area * { visibility: visible; }
          .printable-area { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
            border: none !important; 
          }
          .no-print { display: none !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
    </Box>
  );
}

export default Vehicalwisereport;